import React, { useState, useMemo } from "react";
import { TrendingUp, AlertTriangle, Info, ShieldAlert, ShieldCheck } from "lucide-react";
import { simulaSuperamentoSoglia } from "../../utils/sogliaSimulator";

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value);
}

function parseAmount(str) {
  if (!str) return 0;
  const cleaned = str.replace(/\s/g, "").replace(",", ".");
  return parseFloat(cleaned) || 0;
}

export default function SogliaSimulator({
  incassiAnno,
  profitabilityCoefficient,
  aliquota,
  inpsRate,
  soglia = 85000,
  sogliaExit = 100000,
  darkMode = false,
}) {
  const [importoInput, setImportoInput] = useState("");
  const [costiInput, setCostiInput] = useState("");

  const importo = parseAmount(importoInput);
  const costiDeducibili = parseAmount(costiInput);

  const scenario = useMemo(() => {
    if (importo <= 0) return null;
    return simulaSuperamentoSoglia(
      incassiAnno,
      importo,
      profitabilityCoefficient,
      aliquota / 100,
      inpsRate / 100,
      costiDeducibili,
      soglia,
      sogliaExit
    );
  }, [incassiAnno, importo, profitabilityCoefficient, aliquota, inpsRate, costiDeducibili, soglia, sogliaExit]);

  // Calcola percentuali soglia (soglia ragguagliata nel primo anno)
  const percentualeAttuale = soglia > 0 ? (incassiAnno / soglia) * 100 : 0;
  const percentualeDopo = scenario && soglia > 0 ? (scenario.nuovoTotale / soglia) * 100 : percentualeAttuale;

  const getStatusColor = (status) => {
    switch (status) {
      case "CRITICAL":
        return {
          bg: darkMode ? "bg-red-900/30 border-red-700" : "bg-red-50 border-red-200",
          text: darkMode ? "text-red-300" : "text-red-700",
          icon: <ShieldAlert className={`w-5 h-5 ${darkMode ? "text-red-400" : "text-red-600"}`} />,
          barColor: "bg-red-500",
        };
      case "WARNING":
        return {
          bg: darkMode ? "bg-amber-900/30 border-amber-700" : "bg-amber-50 border-amber-200",
          text: darkMode ? "text-amber-300" : "text-amber-700",
          icon: <AlertTriangle className={`w-5 h-5 ${darkMode ? "text-amber-400" : "text-amber-600"}`} />,
          barColor: "bg-amber-500",
        };
      case "CAUTION":
        return {
          bg: darkMode ? "bg-yellow-900/30 border-yellow-700" : "bg-yellow-50 border-yellow-200",
          text: darkMode ? "text-yellow-300" : "text-yellow-700",
          icon: <Info className={`w-5 h-5 ${darkMode ? "text-yellow-400" : "text-yellow-600"}`} />,
          barColor: "bg-yellow-500",
        };
      default:
        return {
          bg: darkMode ? "bg-emerald-900/30 border-emerald-700" : "bg-emerald-50 border-emerald-200",
          text: darkMode ? "text-emerald-300" : "text-emerald-700",
          icon: <ShieldCheck className={`w-5 h-5 ${darkMode ? "text-emerald-400" : "text-emerald-600"}`} />,
          barColor: "bg-emerald-500",
        };
    }
  };

  return (
    <div className="space-y-5">
      {/* Situazione attuale */}
      <div
        className={`p-4 rounded-xl ${
          darkMode ? "bg-neutral-800" : "bg-neutral-50"
        }`}
      >
        <div
          className={`text-xs font-medium uppercase tracking-wide mb-2 ${
            darkMode ? "text-neutral-500" : "text-neutral-500"
          }`}
        >
          Situazione attuale
        </div>
        <div className="flex items-end justify-between">
          <div>
            <div
              className={`text-2xl font-bold ${
                darkMode ? "text-white" : "text-neutral-900"
              }`}
            >
              {formatCurrency(incassiAnno)}
            </div>
            <div
              className={`text-xs mt-1 ${
                darkMode ? "text-neutral-500" : "text-neutral-500"
              }`}
            >
              Incassi anno corrente
            </div>
          </div>
          <div className="text-right">
            <div
              className={`text-lg font-semibold ${
                percentualeAttuale > 90
                  ? darkMode
                    ? "text-red-400"
                    : "text-red-600"
                  : percentualeAttuale > 80
                  ? darkMode
                    ? "text-amber-400"
                    : "text-amber-600"
                  : darkMode
                  ? "text-emerald-400"
                  : "text-emerald-600"
              }`}
            >
              {percentualeAttuale.toFixed(0)}%
            </div>
            <div
              className={`text-xs ${
                darkMode ? "text-neutral-500" : "text-neutral-500"
              }`}
            >
              della soglia 85k
            </div>
          </div>
        </div>
        {/* Barra progresso */}
        <div
          className={`mt-3 h-2 rounded-full overflow-hidden ${
            darkMode ? "bg-neutral-700" : "bg-neutral-200"
          }`}
        >
          <div
            className={`h-full rounded-full transition-all ${
              percentualeAttuale > 100
                ? "bg-red-500"
                : percentualeAttuale > 88
                ? "bg-amber-500"
                : "bg-emerald-500"
            }`}
            style={{ width: `${Math.min(percentualeAttuale, 100)}%` }}
          />
        </div>
      </div>

      {/* Input fattura ipotetica */}
      <div>
        <label
          htmlFor="sogliaImporto"
          className={`block text-sm font-medium mb-1.5 ${
            darkMode ? "text-neutral-400" : "text-neutral-700"
          }`}
        >
          Fattura ipotetica da aggiungere
        </label>
        <div className="relative">
          <span
            className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
              darkMode ? "text-neutral-500" : "text-neutral-500"
            }`}
          >
            €
          </span>
          <input
            type="text"
            inputMode="decimal"
            id="sogliaImporto"
            value={importoInput}
            onChange={(e) =>
              setImportoInput(e.target.value.replace(/[^0-9,.]/g, ""))
            }
            placeholder="0,00"
            className={`w-full pl-8 pr-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 focus:border-forfy-500 ${
              darkMode
                ? "border-neutral-700 bg-neutral-800 text-white"
                : "border-neutral-300 bg-white text-neutral-900"
            }`}
          />
        </div>
      </div>

      {/* Input costi deducibili */}
      <div>
        <label
          htmlFor="costiDeducibili"
          className={`block text-sm font-medium mb-1.5 ${
            darkMode ? "text-neutral-400" : "text-neutral-700"
          }`}
        >
          Costi annui deducibili stimati (€)
        </label>
        <div className="relative">
          <span
            className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
              darkMode ? "text-neutral-500" : "text-neutral-500"
            }`}
          >
            €
          </span>
          <input
            type="text"
            inputMode="decimal"
            id="costiDeducibili"
            value={costiInput}
            onChange={(e) =>
              setCostiInput(e.target.value.replace(/[^0-9,.]/g, ""))
            }
            placeholder="es. 8000"
            className={`w-full pl-8 pr-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 focus:border-forfy-500 ${
              darkMode
                ? "border-neutral-700 bg-neutral-800 text-white"
                : "border-neutral-300 bg-white text-neutral-900"
            }`}
          />
        </div>
        <p
          className={`mt-1.5 text-xs ${
            darkMode ? "text-neutral-500" : "text-neutral-500"
          }`}
        >
          Usato solo nel confronto con il regime ordinario (affitto studio, software, commercialista, ecc.)
        </p>
      </div>

      {/* Risultato simulazione */}
      {scenario && (
        <>
          {/* Nuova situazione */}
          <div
            className={`p-4 rounded-xl ${
              darkMode ? "bg-neutral-800" : "bg-neutral-50"
            }`}
          >
            <div
              className={`text-xs font-medium uppercase tracking-wide mb-2 ${
                darkMode ? "text-neutral-500" : "text-neutral-500"
              }`}
            >
              Dopo la fattura
            </div>
            <div className="flex items-end justify-between">
              <div>
                <div
                  className={`text-2xl font-bold ${
                    darkMode ? "text-white" : "text-neutral-900"
                  }`}
                >
                  {formatCurrency(scenario.nuovoTotale)}
                </div>
                <div
                  className={`text-xs mt-1 ${
                    darkMode ? "text-forfy-300" : "text-forfy-600"
                  }`}
                >
                  +{formatCurrency(importo)}
                </div>
              </div>
              <div className="text-right">
                <div
                  className={`text-lg font-semibold ${
                    percentualeDopo > 117
                      ? darkMode
                        ? "text-red-400"
                        : "text-red-600"
                      : percentualeDopo > 100
                      ? darkMode
                        ? "text-amber-400"
                        : "text-amber-600"
                      : percentualeDopo > 88
                      ? darkMode
                        ? "text-yellow-400"
                        : "text-yellow-600"
                      : darkMode
                      ? "text-emerald-400"
                      : "text-emerald-600"
                  }`}
                >
                  {percentualeDopo.toFixed(0)}%
                </div>
                <div
                  className={`text-xs ${
                    darkMode ? "text-neutral-500" : "text-neutral-500"
                  }`}
                >
                  della soglia 85k
                </div>
              </div>
            </div>
            {/* Barra progresso con "prima" e "dopo" */}
            <div
              className={`mt-3 h-3 rounded-full overflow-hidden relative ${
                darkMode ? "bg-neutral-700" : "bg-neutral-200"
              }`}
            >
              {/* Barra "prima" */}
              <div
                className="h-full rounded-full absolute left-0 top-0 bg-neutral-400 opacity-40"
                style={{
                  width: `${Math.min(percentualeAttuale, 100)}%`,
                }}
              />
              {/* Barra "dopo" */}
              <div
                className={`h-full rounded-full absolute left-0 top-0 transition-all ${
                  percentualeDopo > 117
                    ? "bg-red-500"
                    : percentualeDopo > 100
                    ? "bg-amber-500"
                    : percentualeDopo > 88
                    ? "bg-yellow-500"
                    : "bg-emerald-500"
                }`}
                style={{
                  width: `${Math.min(percentualeDopo, 100)}%`,
                }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span
                className={`text-[10px] ${
                  darkMode ? "text-neutral-500" : "text-neutral-400"
                }`}
              >
                0€
              </span>
              <span
                className={`text-[10px] ${
                  darkMode ? "text-neutral-500" : "text-neutral-400"
                }`}
              >
                {Math.round(soglia).toLocaleString("it-IT")}€
              </span>
            </div>
          </div>

          {/* Alert status */}
          {scenario.status !== "OK" && (() => {
            const colors = getStatusColor(scenario.status);
            return (
              <div
                className={`flex items-start gap-3 p-4 rounded-xl border ${colors.bg}`}
              >
                {colors.icon}
                <div>
                  <p className={`text-sm font-medium ${colors.text}`}>
                    {scenario.message}
                  </p>
                  {scenario.dettagli.uscita === "IMMEDIATA" && (
                    <p
                      className={`text-xs mt-2 ${
                        darkMode ? "text-red-400" : "text-red-600"
                      }`}
                    >
                      IVA obbligatoria dalla fattura che supera i 100k.
                    </p>
                  )}
                  {scenario.dettagli.annoUscita && (
                    <p
                      className={`text-xs mt-2 ${
                        darkMode ? "text-amber-400" : "text-amber-600"
                      }`}
                    >
                      Anno uscita dal forfettario: {scenario.dettagli.annoUscita}
                    </p>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Tasse forfettario */}
          <div
            className={`rounded-xl border p-4 ${
              darkMode
                ? "border-neutral-800 bg-neutral-800/50"
                : "border-neutral-200 bg-neutral-50"
            }`}
          >
            <h3
              className={`text-xs font-semibold uppercase tracking-wide mb-3 ${
                darkMode ? "text-neutral-500" : "text-neutral-500"
              }`}
            >
              Stima tasse forfettario
            </h3>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>
                  Imponibile
                </span>
                <span className={`font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                  {formatCurrency(scenario.tasseForfettario.imponibile)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>
                  INPS
                </span>
                <span className={`font-medium ${darkMode ? "text-amber-400" : "text-amber-600"}`}>
                  {formatCurrency(scenario.tasseForfettario.inps)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>
                  Imposta {aliquota}%
                </span>
                <span className={`font-medium ${darkMode ? "text-amber-400" : "text-amber-600"}`}>
                  {formatCurrency(scenario.tasseForfettario.imposta)}
                </span>
              </div>
              <div
                className={`flex justify-between pt-2 border-t ${
                  darkMode ? "border-neutral-700" : "border-neutral-200"
                }`}
              >
                <span className={`font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
                  Netto
                </span>
                <span
                  className={`font-bold ${
                    darkMode ? "text-emerald-400" : "text-emerald-600"
                  }`}
                >
                  {formatCurrency(scenario.tasseForfettario.netto)}
                </span>
              </div>
            </div>
          </div>

          {/* Confronto con ordinario (solo se supera 85k) */}
          {scenario.tasseOrdinario && (
            <div
              className={`rounded-xl border p-4 ${
                darkMode
                  ? "border-red-800/50 bg-red-900/20"
                  : "border-red-200 bg-red-50/50"
              }`}
            >
              <h3
                className={`text-xs font-semibold uppercase tracking-wide mb-3 ${
                  darkMode ? "text-red-400" : "text-red-700"
                }`}
              >
                Confronto con regime ordinario
              </h3>
              <div className="space-y-1.5 text-sm">
                {costiDeducibili > 0 && (
                  <div className="flex justify-between">
                    <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>
                      Costi deducibili
                    </span>
                    <span className={`font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                      {formatCurrency(costiDeducibili)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>
                    Reddito imponibile
                  </span>
                  <span className={`font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    {formatCurrency(scenario.tasseOrdinario.reddito)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>
                    IRPEF (a scaglioni)
                  </span>
                  <span className={`font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    {formatCurrency(scenario.tasseOrdinario.irpef)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>
                    Addizionali (~2,2%)
                  </span>
                  <span className={`font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    {formatCurrency(scenario.tasseOrdinario.addizionali)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>
                    INPS ordinario
                  </span>
                  <span className={`font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    {formatCurrency(scenario.tasseOrdinario.inps)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>
                    Aliquota effettiva
                  </span>
                  <span className={`font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    {scenario.tasseOrdinario.aliquotaEffettiva}
                  </span>
                </div>
                <div
                  className={`flex justify-between pt-2 border-t ${
                    darkMode ? "border-red-800/50" : "border-red-200"
                  }`}
                >
                  <span className={`font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
                    Netto ordinario
                  </span>
                  <span
                    className={`font-bold ${
                      darkMode ? "text-emerald-400" : "text-emerald-600"
                    }`}
                  >
                    {formatCurrency(scenario.tasseOrdinario.netto)}
                  </span>
                </div>
                <div
                  className={`flex justify-between pt-2 border-t ${
                    darkMode ? "border-red-800/50" : "border-red-200"
                  }`}
                >
                  <span className={`font-semibold ${darkMode ? "text-red-400" : "text-red-700"}`}>
                    Differenza netto
                  </span>
                  <span
                    className={`font-bold ${
                      scenario.differenzaNetto >= 0
                        ? darkMode
                          ? "text-emerald-400"
                          : "text-emerald-600"
                        : darkMode
                        ? "text-red-400"
                        : "text-red-600"
                    }`}
                  >
                    {scenario.differenzaNetto >= 0 ? "+" : ""}
                    {formatCurrency(scenario.differenzaNetto)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Placeholder */}
      {!scenario && (
        <div
          className={`text-center py-8 rounded-xl border-2 border-dashed ${
            darkMode
              ? "border-neutral-700 text-neutral-500"
              : "border-neutral-300 text-neutral-400"
          }`}
        >
          <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">
            Inserisci un importo per simulare l'impatto
          </p>
        </div>
      )}

      {/* Disclaimer */}
      <div
        className={`flex gap-3 p-3 rounded-lg ${
          darkMode ? "bg-amber-900/20" : "bg-amber-50"
        }`}
      >
        <AlertTriangle
          className={`w-5 h-5 flex-shrink-0 ${
            darkMode ? "text-amber-400" : "text-amber-600"
          }`}
        />
        <p
          className={`text-xs ${
            darkMode ? "text-amber-300" : "text-amber-800"
          }`}
        >
          Simulazione indicativa. Consulta il commercialista per decisioni
          importanti sul regime fiscale.
        </p>
      </div>
    </div>
  );
}
